// Copyright (c) 2024, WSO2 LLC. (https://www.wso2.com). All Rights Reserved.
//
// This software is the property of WSO2 LLC. and its suppliers, if any.
// Dissemination of any information or reproduction of any material contained
// herein in any form is strictly forbidden, unless permitted by WSO2 expressly.
// You may not alter or remove any copyright or other notice from copies of this content.

import { Download } from '@mui/icons-material';
import { Box, Button, Paper, Switch, Tab, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tabs, Tooltip, Typography } from '@mui/material';
import DOMPurify from 'dompurify';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import React, { useEffect, useState } from 'react';
import { Store } from 'react-notifications-component';
import { useParams } from 'react-router-dom';
import LinearLoading from '../components/LinearLoading';
import { RESOURCE_SERVER_URLS } from '../data/configs/spl-config';
import { useGetApi } from '../data/hooks/api';
import * as Types from '../data/utils/types';
import '../styles/TimelogsReport.css';
import { ErrorComponent } from './ErrorPage';
import { ItemNotFoundComponent } from './ItemNotFound';
import { Pie } from 'react-chartjs-2';
import { Chart, ArcElement, Tooltip as TooltipChart, Legend } from 'chart.js';
import PieChart from '../components/PieChart';

Chart.register(ArcElement, TooltipChart, Legend);

type RouteParams = {
   projectId: string;
}

const TimelogsReport: React.FC = () => {

   const { projectId: id } = useParams<RouteParams>();
   const projectId = id ? DOMPurify.sanitize(id) : '';
   const [showReport, setShowReport] = useState(false);
   const [isCustomerReport, setCustomerReport] = useState(false);

   const apiUrl = RESOURCE_SERVER_URLS[0] + `/generate-timelogs-breakdown-report?projectId=${projectId}`;

   const { data, loading, error, getApiData } = useGetApi<Types.TimeLogBreakdownDetails>(
      { url: apiUrl, headers: { "accept": "application/json" } }
   );

   useEffect(() => {
      getApiData();
   }, [])

   useEffect(() => {
      if (data) {
         setShowReport(true);
      }
   }, [data])

   const handleRowClick = (rowData: any) => {
      if (rowData?.caseNumber !== "") {
         window.open(`/support/cases/${rowData.caseNumber}`, '_blank');
      } else {
         Store.addNotification({
            title: "Error!",
            message: "Case not found.",
            type: "danger",
            insert: "top",
            container: "top-right",
            animationIn: ["animate__animated", "animate__fadeIn"],
            animationOut: ["animate__animated", "animate__fadeOut"],
            dismiss: {
               duration: 5000,
               onScreen: true,
            },
         });
      }
   }

   const downloadPDF = () => {
      const input = document.getElementById('timelogs-report');
   
      if (input) {
         html2canvas(input, { scale: 2, logging: true }).then((canvas) => {
            const imgData = canvas.toDataURL('image/png', 1.0);
            const pdf = new jsPDF('p', 'mm', 'a4');
            const pdfWidth = pdf.internal.pageSize.getWidth();
            const pdfHeight = pdf.internal.pageSize.getHeight();
   
            const imgProps = pdf.getImageProperties(imgData);
            const imgHeight = (imgProps.height * pdfWidth) / imgProps.width;
            let heightLeft = imgHeight;
            let position = 0;
   
            pdf.addImage(imgData, 'PNG', 0, position, pdfWidth, imgHeight);
            heightLeft -= pdfHeight;
   
            while (heightLeft >= 0) {
               position = heightLeft - imgHeight;
               pdf.addPage();
               pdf.addImage(imgData, 'PNG', 0, position, pdfWidth, imgHeight);
               heightLeft -= pdfHeight;
            }
   
            pdf.save('Timelogs_Report.pdf');
         });
      }
   };

   const MainView = () => {
      const casesColNameArray = ["Case Number", "Case Id", "Case Type", "Short Description", "State", `${isCustomerReport ? "Consumed Query Time" : "Total Time"}`];
      const casesColAttributeArray = ["caseNumber", "caseId", "caseType", "shortDescription", "state", `${isCustomerReport ? "consumedQueryHours" : "totalHours"}`];

      const timecardColNameArray = ["Logged On", "Logged By", `${isCustomerReport ? "" : "State"}`, "Billable", "Total Time"];
      const timecardColAttributeArray = ["createdOn", "createdBy", `${isCustomerReport ? "" : "state"}`, "isBillable", "total"];

      const filteredCases = isCustomerReport
         ? data?.cases.filter(caseItem => caseItem.caseType === 'Query')
         : data?.cases;

      const filterBillableAndApproved = (timeCards: Types.TimeCardDetails[]) => {
         return isCustomerReport ? timeCards.filter(timeCard => (timeCard.isBillable == 'true' && timeCard.state == "Approved")) : timeCards;
      }

      return (
         <>
            {showReport && (
               <>
                  <Box
                     flex={2}
                     display="flex"
                     justifyContent="flex-end"
                     p={1}
                     marginBottom={'-80px'}
                     marginRight={'30px'}
                     sx={{
                        position: `${showReport ? 'fixed' : 'relative'}`,
                        top: '100px',
                        right: '30px',
                        zIndex: 2000
                     }}
                  >
                     <Tooltip title="Download Report">
                        <Button variant="contained"
                           sx={{
                              color: 'white',
                              ':hover': {
                                 bgcolor: '#e96900',
                                 borderColor: 'primary.main',
                                 color: 'white',
                              },
                              borderRadius: '7px'
                           }}
                           onClick={downloadPDF}
                        >
                           <Download />
                        </Button>
                     </Tooltip>
                  </Box>
                  <Box
                     flex={2}
                     display="flex"
                     justifyContent="flex-start"
                     p={1}
                     marginBottom={'-80px'}
                     marginRight={'30px'}
                     sx={{
                        position: `${showReport ? 'fixed' : 'relative'}`,
                        top: '120px',
                        left: '100px',
                        zIndex: 2000
                     }}
                  >
                  </Box>
               </>
            )}
            <div id='timelogs-report'>
               <Box
                  width={"80%"}
                  padding={"3%"}
                  margin={"auto"}
                  sx={{
                     backgroundColor: "#f7f7f6",
                     marginTop: "15px",
                  }}
               >
                  <Box className='tl-header'>
                     <Typography variant="h4" className='tl-header-title'>
                        {`${isCustomerReport ? "Query Hours Consumption Report" : "Time Report"}`}
                     </Typography>
                  </Box>

                  <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                     <Box id="timelogs-report" className="tl-table-container tl-table-info" sx={{ width: '60%' }}>
                        <TableContainer component={Paper}>
                           <Table size='small'>
                              <TableBody className='tl-table-body'>
                                 <TableRow>
                                    <TableCell><Typography variant='subtitle2' fontWeight={'bold'}>Project Name</Typography></TableCell>
                                    <TableCell>{data?.projectName}</TableCell>
                                 </TableRow>
                                 <TableRow>
                                    <TableCell><Typography variant='subtitle2' fontWeight={'bold'}>Project Key</Typography></TableCell>
                                    <TableCell>{data?.projectKey}</TableCell>
                                 </TableRow>
                                 <TableRow>
                                    <TableCell><Typography variant='subtitle2' fontWeight={'bold'}>Project Type</Typography></TableCell>
                                    <TableCell>{data?.projectType}</TableCell>
                                 </TableRow>
                                 <TableRow>
                                    <TableCell><Typography variant='subtitle2' fontWeight={'bold'}>Remaining Query Hours</Typography></TableCell>
                                    <TableCell>{data?.remainingQueryHours}</TableCell>
                                 </TableRow>
                                 <TableRow>
                                    <TableCell><Typography variant='subtitle2' fontWeight={'bold'}>Total Query Hours</Typography></TableCell>
                                    <TableCell>{data?.totalQueryHours}</TableCell>
                                 </TableRow>
                              </TableBody>
                           </Table>
                        </TableContainer>
                     </Box>
                     <Box sx={{ width: '30%', marginLeft: '20px' }}>
                        <PieChart
                           remainingQueryHours={data?.remainingQueryHours || '0h 0m'}
                           totalQueryHours={data?.totalQueryHours || '0h 0m'}
                        />
                     </Box>
                  </Box>
                  <Box className="tl-table-container">
                     <Typography variant="h6" className="tl-subheading">Project Cases</Typography>
                     <TableContainer component={Paper}>
                        <Table className="tl-case-table">
                           <TableHead className='tl-table-header ' >
                              <TableRow>
                                 {casesColNameArray.map((value: string, index: number) => (
                                    <TableCell key={index} >
                                       {value}
                                    </TableCell>
                                 ))}
                              </TableRow>
                           </TableHead>
                           <TableBody>
                              {filteredCases && filteredCases.length === 0 ? (
                                 <TableRow>
                                    <TableCell colSpan={casesColNameArray.length} align="center">
                                       No data available.
                                    </TableCell>
                                 </TableRow>
                              ) :
                                 filteredCases && filteredCases.map((dataRow: Types.DataStruct, index: number) => (
                                    <TableRow key={index} >
                                       {casesColAttributeArray.map((attributeName: string, index: number) => (

                                          <TableCell key={index}
                                             className={`t-col-size ${(attributeName == 'totalHours' || attributeName == 'consumedQueryHours') ? 'tl-table-cell' : ''}`}
                                             onClick={() => { if (attributeName == 'caseNumber') handleRowClick(dataRow) }}
                                             sx={{
                                                cursor: `${attributeName == 'caseNumber' ? 'pointer' : 'default'}`,
                                                '&:hover': {
                                                   backgroundColor: `${attributeName == 'caseNumber' ? '#97c9ff' : 'inherit'}`,
                                                }
                                             }}
                                          >

                                             {((attributeName == 'totalHours' || attributeName == 'consumedQueryHours') && parseFloat(dataRow[attributeName]) > 0) ?
                                                <a href={`#${dataRow['caseNumber']}`} style={{ marginLeft: '5px' }}> {dataRow[attributeName]}</a>
                                                : dataRow[attributeName]}
                                          </TableCell>

                                       ))}
                                    </TableRow>
                                 ))}
                           </TableBody>
                        </Table>
                     </TableContainer>
                  </Box>

                  {showReport && (
                     <Box sx={{ marginTop: "50px", textAlign: 'center', marginBottom: '20px' }}>
                        <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                           Time Cards
                        </Typography>
                     </Box>
                  )}

                  {filteredCases?.map(caseItem => (
                     filterBillableAndApproved(caseItem.timeCards).length > 0 && (
                        <div id={caseItem.caseNumber}>
                           <Box className="tl-timecard-table-container" key={caseItem.caseId} >
                              <Typography variant="h6" className="tl-subheading">{caseItem.caseNumber + " / " + caseItem.caseId}</Typography>
                              <TableContainer component={Paper}>
                                 <Table className="tl-case-table">
                                    <TableHead className='tl-table-header'>
                                       <TableRow>
                                          {timecardColNameArray.map((value: string, index: number) => (
                                             <TableCell key={index} className={`t-col-size ${value == 'Total Time' ? 'tl-table-cell' : ''}`}>
                                                {value}
                                             </TableCell>
                                          ))}
                                       </TableRow>
                                    </TableHead>
                                    <TableBody>
                                       {filterBillableAndApproved(caseItem.timeCards).map((dataRow: Types.DataStruct, index: number) => (
                                          <TableRow key={index} >
                                             {timecardColAttributeArray.map((attributeName: string, index: number) => (
                                                <TableCell key={index} className={`t-col-size ${attributeName == 'total' ? 'tl-table-cell' : ''}`}>
                                                   {dataRow[attributeName]}
                                                </TableCell>
                                             ))}
                                          </TableRow>
                                       ))}
                                    </TableBody>
                                 </Table>
                              </TableContainer>
                           </Box>
                        </div>
                     )
                  ))}


               </Box>
            </div>

         </>
      )
   }

   const ToggleReport = () => {
      const [value, setValue] = useState(isCustomerReport ? 1 : 0);

      const handleChange = (event: React.SyntheticEvent, newValue: number) => {
         setValue(newValue);
         setCustomerReport(newValue === 1);
      };

      useEffect(() => {
         setValue(isCustomerReport ? 1 : 0);
      }, [isCustomerReport]);

      return (
         <div style={{ border: '1px solid #ff7300', borderRadius: 25, height: '49.5px', width: 'fit-content' }}>
            <Tabs
               sx={{
                  justifyContent: 'flex-start',
                  "& button": { borderRadius: 10, boxShadow: 'insert', zIndex: 1 },
                  "& button.Mui-selected": { color: '#fff' }
               }}
               value={value}
               onChange={handleChange}
               TabIndicatorProps={{ sx: { backgroundColor: '#ff7300', height: '100%', borderRadius: 10 } }}
            >
               <Tab label="Internal Report" />
               <Tab label="Customer Report" />
            </Tabs>
         </div>
      );
   };

   return (
      <>
         {loading ? (
            <>
               <LinearLoading />
               {<Box className="blur">{MainView()}</Box>}
            </>
         ) : error ? (
            error.status === 404 ? (
               <ItemNotFoundComponent />
            ) : (
               <ErrorComponent />
            )
         ) : (
            <>
               {showReport ? (
                  <>
                     <ToggleReport />
                     <MainView />
                  </>
               ) : null}
               {!showReport && <Box className="blur">{MainView()}</Box>}
            </>
         )}
      </>
   );
};

export default TimelogsReport;
